import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  DollarSign, 
  ShoppingCart, 
  Package,
  Users,
  ArrowUpRight,
  Mail,
  Bell,
  User,
  X,
  Eye,
  FileText,
  Bot,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order, Payment, RawMaterial, Customer } from '../../services/dataService';
import { useNotifications } from '../../hooks/useNotifications';


interface DashboardStats {
  totalRevenue: number;
  todayRevenue: number;
  todayOrders: number;
  lowStockItems: number;
  activeCustomers: number;
  totalOrders: number;
  averageOrderValue: number;
  inventoryValue: number;
}

const Dashboard: React.FC = () => {
  const { userData, tenantData } = useAuth();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    todayRevenue: 0,
    todayOrders: 0,
    lowStockItems: 0,
    activeCustomers: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    inventoryValue: 0
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Real-time data states
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);

  // Modal state
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Check if user needs to see welcome modal (no subscription)
  useEffect(() => {
    if (tenantData && !tenantData.subscription) {
      setShowWelcomeModal(true);
    }
  }, [tenantData]);

  // Calculate dashboard stats whenever data changes
  const calculateStats = useCallback(() => {
    try {
      console.log('Calculating stats with data:', { 
        ordersCount: orders.length, 
        paymentsCount: payments.length, 
        materialsCount: rawMaterials.length, 
        customersCount: customers.length 
      });

      // Calculate today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Filter today's orders
      const todayOrders = orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= today && orderDate < tomorrow;
      });

      // Calculate revenue
      const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      
      // Calculate today's revenue
      const todayPayments = payments.filter(payment => {
        if (!payment.createdAt) return false;
        const paymentDate = new Date(payment.createdAt);
        return paymentDate >= today && paymentDate < tomorrow;
      });
      const todayRevenue = todayPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

      // Calculate low stock items
      const lowStockItems = rawMaterials.filter(material => 
        material.currentStock <= material.minStock
      ).length;

      // Calculate active customers (customers with orders in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentOrderCustomers = new Set(
        orders
          .filter(order => order.createdAt && new Date(order.createdAt) >= thirtyDaysAgo)
          .map(order => order.customer)
          .filter(customer => customer)
      );
      const activeCustomers = recentOrderCustomers.size;

      // Calculate average order value
      const totalOrderValue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
      const averageOrderValue = orders.length > 0 ? totalOrderValue / orders.length : 0;

      // Calculate inventory value
      const inventoryValue = rawMaterials.reduce((sum, material) => 
        sum + (material.currentStock * (material.costPerSubUnit || material.costPerUnit || 0)), 0
      );

      // Get recent orders (last 10)
      const sortedOrders = [...orders]
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 10);

      setStats({
        totalRevenue,
        todayRevenue,
        todayOrders: todayOrders.length,
        lowStockItems,
        activeCustomers,
        totalOrders: orders.length,
        averageOrderValue,
        inventoryValue
      });

      setRecentOrders(sortedOrders);
      setLastUpdated(new Date());

      console.log('Stats calculated:', {
        totalRevenue,
        todayRevenue,
        todayOrders: todayOrders.length,
        lowStockItems,
        activeCustomers,
        totalOrders: orders.length
      });
    } catch (error) {
      console.error('Error calculating stats:', error);
    }
  }, [orders, payments, rawMaterials, customers]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!tenantData?.id) {
      console.log('No tenant ID available for subscriptions');
      setLoading(false);
      return;
    }

    console.log('Setting up real-time subscriptions for tenant:', tenantData.id);

    try {
      const unsubscribe = dataService.subscribeToDashboardData(tenantData.id, {
        onOrdersUpdate: (newOrders) => {
          console.log('Orders updated:', newOrders.length);
          setOrders(newOrders);
          setDataLoaded(true);
          setLoading(false); // Set loading to false when we get data
        },
        onPaymentsUpdate: (newPayments) => {
          console.log('Payments updated:', newPayments.length);
          setPayments(newPayments);
          setLoading(false); // Set loading to false when we get data
        },
        onRawMaterialsUpdate: (newMaterials) => {
          console.log('Raw materials updated:', newMaterials.length);
          setRawMaterials(newMaterials);
          setLoading(false); // Set loading to false when we get data
        },
        onCustomersUpdate: (newCustomers) => {
          console.log('Customers updated:', newCustomers.length);
          setCustomers(newCustomers);
          setLoading(false); // Set loading to false when we get data
        }
      });

      // Cleanup subscriptions on unmount
      return () => {
        console.log('Cleaning up dashboard subscriptions...');
        unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up subscriptions:', error);
      setLoading(false);
    }
  }, [tenantData?.id]);

  // Recalculate stats whenever data changes
  useEffect(() => {
    if (dataLoaded) {
      calculateStats();
    }
  }, [calculateStats, dataLoaded]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'preparing': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
      case 'ready': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'completed': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      case 'active': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getOrderStatus = (order: Order) => {
    if (order.isPaid) return 'completed';
    if (order.items && order.items.some(item => item.status === 'preparing')) return 'preparing';
    if (order.items && order.items.some(item => item.status === 'completed')) return 'ready';
    return 'active';
  };

  const formatTimeAgo = (date: Date | string) => {
    const now = new Date();
    const orderDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getOrderItemsSummary = (order: Order) => {
    if (!order.items || order.items.length === 0) return 'No items';
    
    const itemNames = order.items.map(item => item.menuItemName);
    if (itemNames.length <= 2) {
      return itemNames.join(', ');
    }
    return `${itemNames.slice(0, 2).join(', ')} +${itemNames.length - 2} more`;
  };

  // Card click handler
  const handleCardClick = (card: string) => setExpandedCard(card);
  const handleCloseModal = () => setExpandedCard(null);
  const handleNotificationClick = () => navigate('/notifications');

  if (loading) {
    console.log('Dashboard is in loading state');
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  console.log('Dashboard rendering with:', { loading, dataLoaded, stats, recentOrders: recentOrders.length });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-4 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2 sm:gap-0">
          <div className="animate-slide-in-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              Welcome back, {userData?.firstName || 'Admin'}!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
              Here's what's happening with your cafe today
            </p>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4 animate-slide-in-right">
            <button 
              onClick={() => navigate('/today-report')}
              className="flex items-center space-x-2 relative p-2 sm:p-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white backdrop-blur-md rounded-xl shadow-sm border border-primary-200 dark:border-primary-700 hover:shadow-lg hover:scale-110 transition-all duration-500 ease-out hover:rotate-3"
              title="View Today's Report"
            >
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:scale-110" />
              <span className="hidden sm:inline font-semibold">View Report</span>
            </button>
            <button className="relative p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-110 transition-all duration-500 ease-out hover:rotate-3">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 transition-transform duration-300 group-hover:scale-110" />
            </button>
            <button 
              onClick={handleNotificationClick}
              className="relative p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-110 transition-all duration-500 ease-out hover:-rotate-3"
            >
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 transition-transform duration-300 group-hover:scale-110" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full animate-pulse flex items-center justify-center">
                  <span className="text-xs text-white font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                </span>
              )}
            </button>
            <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center animate-bounce">
                <User className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{userData?.firstName} {userData?.lastName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{userData?.email}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Last Updated */}
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-1 sm:px-4 sm:py-2 inline-block">
          Last updated: {lastUpdated.toLocaleTimeString()} • Real-time updates enabled
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6 sm:mb-8">
        {/* Today's Revenue */}
        <div onClick={() => handleCardClick('todayRevenue')} tabIndex={0} role="button"
          className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 dark:text-green-400 group-hover:rotate-12 transition-transform duration-500" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">Today's Revenue</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors duration-300">₹{stats.todayRevenue.toFixed(0)}</p>
            <div className="flex items-center">
              <span className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 group-hover:scale-105 transition-transform duration-300">
                {stats.todayOrders} orders today
              </span>
            </div>
          </div>
          <div className="absolute top-0 left-0 right-0 h-0.5 sm:h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-t-lg sm:rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out transform scale-x-0 group-hover:scale-x-100 origin-left"></div>
        </div>

        {/* Total Revenue */}
        <div onClick={() => handleCardClick('totalRevenue')} tabIndex={0} role="button"
          className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400 group-hover:rotate-12 transition-transform duration-500" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">Total Revenue</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300">₹{stats.totalRevenue.toFixed(0)}</p>
            <div className="flex items-center">
              <span className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform duration-300">
                {stats.totalOrders} total orders
              </span>
            </div>
          </div>
          <div className="absolute top-0 left-0 right-0 h-0.5 sm:h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-t-lg sm:rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out transform scale-x-0 group-hover:scale-x-100 origin-left"></div>
        </div>

        {/* Low Stock Items */}
        <div onClick={() => handleCardClick('lowStock')} tabIndex={0} role="button"
          className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-200 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 dark:text-orange-400 group-hover:rotate-12 transition-transform duration-500" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">Low Stock Items</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors duration-300">{stats.lowStockItems}</p>
            <div className="flex items-center">
              <span className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400 group-hover:scale-105 transition-transform duration-300">
                Need restocking
              </span>
            </div>
          </div>
          <div className="absolute top-0 left-0 right-0 h-0.5 sm:h-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-t-lg sm:rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out transform scale-x-0 group-hover:scale-x-100 origin-left"></div>
        </div>

        {/* Active Customers */}
        <div onClick={() => handleCardClick('activeCustomers')} tabIndex={0} role="button"
          className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-300 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 dark:text-purple-400 group-hover:rotate-12 transition-transform duration-500" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">Active Customers</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors duration-300">{stats.activeCustomers}</p>
            <div className="flex items-center">
              <span className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 group-hover:scale-105 transition-transform duration-300">
                Last 30 days
              </span>
            </div>
          </div>
          <div className="absolute top-0 left-0 right-0 h-0.5 sm:h-1 bg-gradient-to-r from-purple-400 to-purple-600 rounded-t-lg sm:rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out transform scale-x-0 group-hover:scale-x-100 origin-left"></div>
        </div>
      </div>

      {/* AI Assistant Card */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 ease-out animate-fade-in-up animation-delay-400 mb-6 sm:mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Bot className="w-8 h-8" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <h3 className="text-xl font-bold">AI Business Assistant</h3>
              <p className="text-purple-100 text-sm">Live</p>
            </div>
          </div>
          <Sparkles className="w-6 h-6 text-yellow-300 animate-bounce" />
        </div>
        
        <div className="space-y-3">
          <p className="text-purple-100">
            Get instant insights about your business with AI-powered analytics. Ask questions about sales, inventory, customers, and more!
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white/20 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4" />
                <span>Sales Analytics</span>
              </div>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4" />
                <span>Inventory</span>
              </div>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Customers</span>
              </div>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-4 h-4" />
                <span>Reports</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2">
            <span className="text-purple-200 text-sm">Try asking: "What are today's sales?"</span>
            <button 
              onClick={() => {
                console.log('🎯 Dashboard: Dispatching open-chatbot event');
                window.dispatchEvent(new CustomEvent('open-chatbot'));
              }}
              className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
            >
              Try Now
            </button>
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 mb-6 sm:mb-8">
        {/* Average Order Value */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 ease-out animate-fade-in-up animation-delay-400">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg hover:scale-110 hover:rotate-12 transition-all duration-500">
              <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-white hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Average Order Value</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">₹{stats.averageOrderValue.toFixed(0)}</p>
          </div>
        </div>

        {/* Inventory Value */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 ease-out animate-fade-in-up animation-delay-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center shadow-lg hover:scale-110 hover:rotate-12 transition-all duration-500">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Inventory Value</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">₹{stats.inventoryValue.toFixed(0)}</p>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in-up animation-delay-600">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Recent Orders
          </h2>
          <button 
            onClick={() => navigate('/order-history')}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium transition-all duration-500 ease-out hover:shadow-xl hover:scale-110 hover:rotate-1"
          >
            <span>View All</span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>

        {recentOrders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <ShoppingCart className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No orders yet</h3>
            <p className="text-gray-600 dark:text-gray-400">Orders will appear here as they are placed.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Order ID</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Customer</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Items</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Total</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Time</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order, index) => {
                    const status = getOrderStatus(order);
                    return (
                      <tr key={order.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-300 ease-out hover:scale-[1.01] animate-fade-in-up" style={{animationDelay: `${0.7 + index * 0.1}s`}}>
                        <td className="py-4 px-6 font-medium text-gray-900 dark:text-white">
                          {order.id || 'Unknown Order'}
                        </td>
                        <td className="py-4 px-6 text-gray-700 dark:text-gray-300">
                          {order.customer || 'Unknown'}
                        </td>
                        <td className="py-4 px-6 text-gray-700 dark:text-gray-300">
                          {getOrderItemsSummary(order)}
                        </td>
                        <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">
                          ₹{(order.total || 0).toFixed(2)}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)} transition-all duration-300 hover:scale-110`}>
                            {status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400">
                          {order.createdAt ? formatTimeAgo(order.createdAt) : 'Unknown'}
                        </td>
                        <td className="py-4 px-6">
                          <button
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className="flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/40 transition-all duration-200 animate-button-hover"
                            title="View Order Details"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden p-4 space-y-3">
              {recentOrders.map((order, index) => {
                const status = getOrderStatus(order);
                return (
                  <div 
                    key={order.id} 
                    className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 animate-card-enter hover:shadow-md transition-all duration-300"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {order.id || 'Unknown Order'}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{order.customer || 'Unknown'}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </div>

                    {/* Order Details */}
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Items:</span>
                        <span className="text-gray-900 dark:text-white font-medium">{getOrderItemsSummary(order)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Total:</span>
                        <span className="text-gray-900 dark:text-white font-semibold">₹{(order.total || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Time:</span>
                        <span className="text-gray-900 dark:text-white">{order.createdAt ? formatTimeAgo(order.createdAt) : 'Unknown'}</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/40 transition-all duration-200 animate-button-hover"
                      >
                        <Eye className="w-4 h-4 mr-1.5" />
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Today's Report Card */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in-up animation-delay-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Today's Report</h2>
              <p className="text-gray-600 dark:text-gray-400">Complete overview of today's activities</p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/today-report')}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-medium transition-all duration-500 ease-out hover:shadow-xl hover:scale-110 hover:rotate-1"
          >
            <span>View Report</span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayOrders}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Orders</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">₹{stats.todayRevenue.toFixed(0)}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Revenue</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.activeCustomers}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Active Customers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.lowStockItems}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Low Stock Items</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Overlay for Expanded Card */}
      {expandedCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-[90vw] max-w-md sm:max-w-lg scale-95 animate-scale-in">
            <button onClick={handleCloseModal} className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <X className="w-5 h-5 text-gray-500 dark:text-gray-300" />
            </button>
            {/* Expanded Card Content */}
            {expandedCard === 'todayRevenue' && (
              <>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Today's Revenue</h2>
                    <p className="text-gray-500 dark:text-gray-400">Detailed breakdown for today</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">₹{stats.todayRevenue.toFixed(0)}</div>
                <div className="text-sm text-green-600 dark:text-green-400 mb-2">{stats.todayOrders} orders today</div>
                {/* Orders Table */}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Today's Orders</h3>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400">
                          <th className="py-1 pr-2">Order ID</th>
                          <th className="py-1 pr-2">Amount</th>
                          <th className="py-1 pr-2">Time</th>
                          <th className="py-1">Customer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.filter(order => {
                          if (!order.createdAt) return false;
                          const orderDate = new Date(order.createdAt);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const tomorrow = new Date(today);
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return orderDate >= today && orderDate < tomorrow;
                        }).map(order => (
                          <tr key={order.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-1 pr-2 font-medium text-gray-900 dark:text-white">{order.id}</td>
                            <td className="py-1 pr-2 text-green-700 dark:text-green-300">₹{(order.total || 0).toFixed(0)}</td>
                            <td className="py-1 pr-2 text-gray-600 dark:text-gray-300">{order.createdAt ? new Date(order.createdAt).toLocaleTimeString() : '-'}</td>
                            <td className="py-1 text-gray-600 dark:text-gray-300">{order.customer || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            {expandedCard === 'totalRevenue' && (
              <>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Total Revenue</h2>
                    <p className="text-gray-500 dark:text-gray-400">All-time revenue summary</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">₹{stats.totalRevenue.toFixed(0)}</div>
                <div className="text-sm text-blue-600 dark:text-blue-400 mb-2">{stats.totalOrders} total orders</div>
                {/* Top Customers */}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Top Customers</h3>
                  <ul className="max-h-32 overflow-y-auto space-y-1">
                    {(() => {
                      // Count orders per customer
                      const customerMap = new Map();
                      orders.forEach(order => {
                        if (order.customer) {
                          customerMap.set(order.customer, (customerMap.get(order.customer) || 0) + (order.total || 0));
                        }
                      });
                      return Array.from(customerMap.entries())
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([customer, total]) => (
                          <li key={customer} className="flex justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-200">{customer}</span>
                            <span className="text-blue-700 dark:text-blue-300 font-semibold">₹{total.toFixed(0)}</span>
                          </li>
                        ));
                    })()}
                  </ul>
                </div>
              </>
            )}
            {expandedCard === 'lowStock' && (
              <>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Low Stock Items</h2>
                    <p className="text-gray-500 dark:text-gray-400">Items needing restocking</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{stats.lowStockItems}</div>
                <div className="text-sm text-orange-600 dark:text-orange-400 mb-2">Need restocking</div>
                {/* Low Stock Table */}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Low Stock Materials</h3>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400">
                          <th className="py-1 pr-2">Material</th>
                          <th className="py-1 pr-2">Current</th>
                          <th className="py-1 pr-2">Min</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rawMaterials.filter(m => m.currentStock <= m.minStock).map(m => (
                          <tr key={m.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-1 pr-2 font-medium text-gray-900 dark:text-white">{m.name}</td>
                            <td className="py-1 pr-2 text-orange-700 dark:text-orange-300">{m.currentStock}</td>
                            <td className="py-1 pr-2 text-gray-600 dark:text-gray-300">{m.minStock}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            {expandedCard === 'activeCustomers' && (
              <>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Active Customers</h2>
                    <p className="text-gray-500 dark:text-gray-400">Customers in last 30 days</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{stats.activeCustomers}</div>
                <div className="text-sm text-purple-600 dark:text-purple-400 mb-2">Last 30 days</div>
                {/* Active Customers List */}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Recent Customers</h3>
                  <ul className="max-h-32 overflow-y-auto space-y-1">
                    {(() => {
                      // Find customers with orders in last 30 days
                      const thirtyDaysAgo = new Date();
                      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                      const customerMap = new Map();
                      orders.filter(order => order.createdAt && new Date(order.createdAt) >= thirtyDaysAgo)
                        .forEach(order => {
                          if (order.customer) {
                            customerMap.set(order.customer, (customerMap.get(order.customer) || 0) + 1);
                          }
                        });
                      return Array.from(customerMap.entries())
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([customer, count]) => (
                          <li key={customer} className="flex justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-200">{customer}</span>
                            <span className="text-purple-700 dark:text-purple-300 font-semibold">{count} orders</span>
                          </li>
                        ));
                    })()}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard; 